import { ReactElement, memo } from "react";
import { NativeScrollEvent, NativeSyntheticEvent } from "react-native";

import ListEmpty from "@/components/molecules/ListEmpty";
import SkeletonList from "@/components/organisms/SkeletonList";

import ItemSeparator from "@/components/molecules/ItemSeparator";
import { FlashList, ListRenderItem } from "@/components/atoms/FlashList";
import { RefreshControl } from "@/components/atoms/RefreshControl";
import { PlayerCardData } from "@/types/playerCardTypes";
import defaultExtractKey from "@/utils/defaultExtractKey";

/**
 * Props for PlayerListView component
 */
interface IPlayerList {
  loading: boolean;
  refreshing: boolean;
  players: PlayerCardData[];
  heightRecommendation?: number;
  isSmallScreen: boolean;
  onLoadMore: () => void;
  /** Fired on list scroll; used by the container to detect the first user scroll */
  onScroll?: (event: NativeSyntheticEvent<NativeScrollEvent>) => void;
  onRefresh: () => void;
  renderItem: ListRenderItem<PlayerCardData>;
  renderListFooterComponent: () => ReactElement | null;
  /** Custom component to render when list is empty and not loading */
  renderListEmptyComponent?: () => ReactElement;
}

/**
 * View component for player list with pull-to-refresh and infinite scroll
 * @param root0 - Component props
 * @param root0.loading - Whether initial player data is loading
 * @param root0.refreshing - Whether list is being refreshed via pull-to-refresh
 * @param root0.players - Array of player fragments to display
 * @param root0.heightRecommendation - Recommended height for bottom padding
 * @param root0.isSmallScreen - Whether rendering on mobile screen size
 * @param root0.onLoadMore - Callback invoked when scrolling near end to load more players
 * @param root0.onScroll - Callback invoked on list scroll; lets the container detect the first user scroll
 * @param root0.onRefresh - Callback invoked when user pulls to refresh the list
 * @param root0.renderItem - Function to render each player item in the list
 * @param root0.renderListFooterComponent - Function to render loading indicator at list bottom
 * @param root0.renderListEmptyComponent - Custom empty state renderer with manual add option
 */
const PlayerListView = ({
  loading,
  refreshing,
  players,
  heightRecommendation,
  isSmallScreen,
  onLoadMore,
  onScroll,
  onRefresh,
  renderItem,
  renderListFooterComponent,
  renderListEmptyComponent,
}: IPlayerList) => (
  <FlashList
    className="flex-1"
    contentContainerClassName={`flex-grow py-3 ${!isSmallScreen ? "px-2" : ""}`}
    contentContainerStyle={{ paddingBottom: heightRecommendation }}
    data={players}
    refreshing={refreshing}
    keyExtractor={defaultExtractKey}
    scrollEventThrottle={16}
    renderItem={renderItem}
    showsVerticalScrollIndicator={false}
    ListEmptyComponent={
      loading ? (
        <SkeletonList type="player" />
      ) : renderListEmptyComponent ? (
        renderListEmptyComponent()
      ) : (
        <ListEmpty />
      )
    }
    ListFooterComponent={renderListFooterComponent}
    ItemSeparatorComponent={ItemSeparator}
    onEndReached={onLoadMore}
    onEndReachedThreshold={0.5}
    onScroll={onScroll}
    onRefresh={onRefresh}
    refreshControl={
      <RefreshControl
        refreshing={refreshing}
        onRefresh={onRefresh}
        colors={["white"]}
        tintColor="white"
      />
    }
  />
);

PlayerListView.displayName = "PlayerListView";

export default memo(PlayerListView);
